/**
 * 🎯 V291.3.1: Google Play Compliant Verify Paywall Component
 *
 * CRITICAL COMPLIANCE FEATURES:
 * - Auto-renewal disclosure at TOP (visible without scrolling)
 * - DYNAMIC price from Google Play API (V291.3.1 fix)
 * - "Restore Purchases" button
 * - Privacy Policy and Terms of Service links
 * - High contrast text (gray-700 or darker)
 * - Mobile-first responsive design
 *
 * Purpose: Single upgrade/paywall component for Google Play (Android)
 *
 * Location: src/features/subscription/VerifyPaywall.tsx
 *
 * Usage:
 * import { VerifyPaywall } from '@/features/subscription/VerifyPaywall';
 *
 * <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
 *   <DialogContent className="max-w-md p-0 bg-transparent border-0 shadow-none">
 *     <VerifyPaywall onClose={() => setShowPaywall(false)} />
 *   </DialogContent>
 * </Dialog>
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IS_WEB, IS_ANDROID, IS_IOS, PAYMENT_PROVIDER, IS_BILLING_DISABLED } from '@/config/channel';
import { Capacitor } from '@capacitor/core'; // V287: Runtime platform detection
import { Browser } from '@capacitor/browser';
import { Button } from '@/components/ui/button';
import { Shield, Lock, Smartphone, RotateCcw, User, LogOut, AlertCircle, ClipboardList, ExternalLink, X, TrendingUp, Sparkles, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNetworkData } from '@/contexts/NetworkDataContext';
import { toast } from 'sonner';
import { UnifiedSubscriptionService } from '@/services/subscription/UnifiedSubscriptionService';
import { useEntitlements } from '@/hooks/useEntitlements'; // V210.2: Use centralized entitlements
import { EntitlementService } from '@/services/entitlement/EntitlementService'; // V210.2: For cache clearing
import { VerifiedDialogLayout } from './components/VerifiedDialogLayout';
import { getVerifiedPaywallBenefits } from './constants/verifiedBenefits';
import type { VerifiedDialogBullet } from './components/VerifiedDialogLayout';
import { supabase } from '@/lib/supabaseGuard';
import { BillingManager } from '@/billing/BillingManager';
import { GOOGLE_PLAY_VERIFIED_PRODUCT_ID, APPLE_VERIFIED_PRODUCT_ID } from '@/config/subscriptions';
import { invokeAuthedEdge, InvokeNotAuthenticatedError } from '@/lib/supabase/invokeAuthedEdge';
// V291.2: Import verification helper for unified UI gate
import { isVerifiedUI } from '@/utils/verificationHelpers';
// V300: Identity-Aware Billing
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { trackEvent, AnalyticsEvents } from '@/analytics';
import i18n from '@/i18n';

interface VerifyPaywallProps {
  onClose?: () => void;
}

interface ValidatePurchaseEdgeResponse {
  verified?: boolean;
  entitlementState?: 'idle' | 'pending' | 'active' | 'failed';
  error?: string;
}

type IosProductFetchStatus = 'loading' | 'ready' | 'failed';

const MODAL_PANEL_MAX_HEIGHT =
  'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 28px)';

type SubscriptionManageMode = 'ios' | 'android' | 'generic';

export function resolveSubscriptionManageMode(): SubscriptionManageMode {
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
  }
  return 'generic';
}

async function openAppleSubscriptionSettings(): Promise<void> {
  const url = 'https://apps.apple.com/account/subscriptions';
  try {
    await Browser.open({ url });
  } catch {
    try {
      window.open(url, '_blank');
    } catch {
      toast.error(i18n.t('verifiedPaywall.toasts.openSettingsFailed'), { duration: 5000 });
    }
  }
}

async function openGooglePlaySubscriptionSettings(): Promise<void> {
  const url =
    'https://play.google.com/store/account/subscriptions?package=com.blissnet.app&sku=verified_monthly';
  try {
    await Browser.open({ url });
  } catch {
    try {
      window.open(url, '_blank');
    } catch {
      toast.error(i18n.t('verifiedPaywall.toasts.openSettingsFailed'), { duration: 5000 });
    }
  }
}

export async function openSubscriptionStoreSettings(onGenericWeb?: () => void): Promise<void> {
  const manageMode = resolveSubscriptionManageMode();
  if (manageMode === 'ios') {
    await openAppleSubscriptionSettings();
    return;
  }
  if (manageMode === 'android') {
    await openGooglePlaySubscriptionSettings();
    return;
  }
  onGenericWeb?.();
}

export async function contactSubscriptionSupport(): Promise<void> {
  const supportEmail = 'blissnetapp@gmail.com';
  try {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(supportEmail);
      toast.success(i18n.t('verifiedPaywall.toasts.supportEmailCopied'), { duration: 4000 });
    } else {
      window.location.href = `mailto:${supportEmail}?subject=Subscription%20Support%20Request`;
    }
  } catch {
    try {
      window.location.href = `mailto:${supportEmail}?subject=Subscription%20Support%20Request`;
    } catch {
      toast.error(i18n.t('verifiedPaywall.toasts.supportUnavailable'), { duration: 5000 });
    }
  }
}

function useVerifiedStatusBenefits(): VerifiedDialogBullet[] {
  const { t, i18n } = useTranslation();
  return useMemo(
    () => [
      {
        icon: <Shield className="w-3 h-3 text-white" />,
        label: t('verifiedPaywall.verified.benefits.badge.title'),
        description: t('verifiedPaywall.verified.benefits.badge.description'),
      },
      {
        icon: <Users className="w-3 h-3 text-white" />,
        label: t('verifiedPaywall.verified.benefits.ranking.title'),
        description: t('verifiedPaywall.verified.benefits.ranking.description'),
      },
      {
        icon: <TrendingUp className="w-3 h-3 text-white" />,
        label: t('verifiedPaywall.verified.benefits.trust.title'),
        description: t('verifiedPaywall.verified.benefits.trust.description'),
      },
      {
        icon: <Sparkles className="w-3 h-3 text-white" />,
        label: t('verifiedPaywall.verified.benefits.support.title'),
        description: t('verifiedPaywall.verified.benefits.support.description'),
      },
    ],
    [t, i18n.language]
  );
}

function PaywallDialogShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 w-full items-center justify-center p-3">
      <div
        className="flex min-h-0 w-[min(calc(100vw-24px),100%)] max-w-md md:max-w-lg flex-col overflow-hidden rounded-2xl md:rounded-3xl shadow-2xl"
        style={{ maxHeight: MODAL_PANEL_MAX_HEIGHT }}
      >
        {children}
      </div>
    </div>
  );
}

interface FixedSubscriptionModalProps {
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
}

/** Fixed viewport overlay — header/body/footer; only body scrolls. No sticky. */
function FixedSubscriptionModal({ header, body, footer }: FixedSubscriptionModalProps) {
  const { i18n } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[10102] flex items-start md:items-center justify-center overflow-hidden overscroll-none"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        paddingLeft: '12px',
        paddingRight: '12px',
      }}
    >
      <div
        key={i18n.language}
        className="flex w-[min(calc(100vw-24px),480px)] flex-col overflow-hidden rounded-2xl shadow-2xl bg-gradient-to-b from-slate-950 via-slate-900 to-[#050011] border border-white/10"
        style={{ maxHeight: MODAL_PANEL_MAX_HEIGHT }}
      >
        <div className="flex-shrink-0">{header}</div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{body}</div>
        <div className="flex-shrink-0">{footer}</div>
      </div>
    </div>
  );
}

const STATUS_BULLET_COLORS = [
  { bg: 'bg-slate-900/50', border: 'border-white/10', iconBg: 'bg-gradient-to-br from-emerald-400 to-teal-600' },
  { bg: 'bg-slate-900/50', border: 'border-white/10', iconBg: 'bg-gradient-to-br from-blue-400 to-cyan-600' },
  { bg: 'bg-slate-900/50', border: 'border-white/10', iconBg: 'bg-gradient-to-br from-purple-400 to-violet-600' },
  { bg: 'bg-slate-900/50', border: 'border-white/10', iconBg: 'bg-gradient-to-br from-pink-400 to-rose-600' },
] as const;

function CompactDialogIcon({ icon }: { icon: React.ReactNode }) {
  return (
    <div className="relative inline-flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-fuchsia-500/20 rounded-full blur-lg opacity-35 scale-105" />
      <div className="relative bg-gradient-to-br from-purple-500/95 via-pink-500/95 to-fuchsia-500/95 rounded-full p-1.5 shadow-md border border-white/20">
        <div className="text-white [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
      </div>
    </div>
  );
}

function ModalCloseButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-2 end-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-800/70 hover:bg-slate-700/80 border border-white/10 text-white/70 hover:text-white"
      aria-label={label}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

export interface VerifiedStatusDialogProps {
  showRenewalNote: boolean;
  onManage: () => void;
  onDone: () => void;
  onClose?: () => void;
}

export function VerifiedStatusDialog({
  showRenewalNote,
  onManage,
  onDone,
  onClose,
}: VerifiedStatusDialogProps) {
  const { t } = useTranslation();
  const bullets = useVerifiedStatusBenefits();
  const closeLabel = t('connectionModals.connect.close');

  return (
    <FixedSubscriptionModal
      header={
        <div className="relative px-4 pt-2 pb-1.5">
          {onClose && <ModalCloseButton onClick={onClose} label={closeLabel} />}
          <div className="flex justify-center mb-0.5">
            <CompactDialogIcon icon={<Shield className="text-white" />} />
          </div>
          <h2 className="text-sm font-bold text-center text-white leading-tight" dir="auto">
            {t('verifiedPaywall.verified.title')}
          </h2>
          <p className="text-center text-[11px] text-gray-400 mt-0.5" dir="auto">
            {t('verifiedPaywall.verified.subtitle')}
          </p>
        </div>
      }
      body={
        <div className="px-4 pb-1.5">
          <div className="grid grid-cols-2 gap-1">
            {bullets.map((bullet, index) => {
              const colors = STATUS_BULLET_COLORS[index % STATUS_BULLET_COLORS.length];
              return (
                <div
                  key={`status-benefit-${index}`}
                  className={`flex flex-col gap-1 p-2 rounded-lg ${colors.bg} ${colors.border} border min-h-0`}
                >
                  <div className="flex items-center gap-1.5">
                    {bullet.icon && (
                      <div className={`${colors.iconBg} rounded-md p-1 shrink-0`}>
                        <div className="text-white w-3 h-3 flex items-center justify-center [&>svg]:w-3 [&>svg]:h-3">
                          {bullet.icon}
                        </div>
                      </div>
                    )}
                    <div className="font-semibold text-white text-[11px] leading-tight line-clamp-2" dir="auto">
                      {bullet.label}
                    </div>
                  </div>
                  {bullet.description && (
                    <div className="text-[10px] text-white/65 leading-tight line-clamp-2 ps-0.5" dir="auto">
                      {bullet.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {showRenewalNote && (
            <p className="text-center text-[11px] text-gray-400 mt-2" dir="auto">
              {t('verifiedPaywall.verified.renewalNote')}
            </p>
          )}
        </div>
      }
      footer={
        <div
          className="px-4 pt-2 space-y-1.5 border-t border-white/10 bg-slate-950/80"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
          <button
            type="button"
            onClick={onManage}
            className="w-full text-center text-xs text-purple-300 hover:text-white underline underline-offset-2 py-0.5"
            dir="auto"
          >
            {t('verifiedPaywall.verified.manageLink')}
          </button>
          <Button
            onClick={onDone}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold h-9 text-sm rounded-xl"
          >
            {t('verifiedPaywall.verified.doneButton')}
          </Button>
        </div>
      }
    />
  );
}

export interface ManageSubscriptionDialogProps {
  onOpenStoreSettings: () => void;
  onContactSupport: () => void;
  onClose: () => void;
}

export function ManageSubscriptionDialog({
  onOpenStoreSettings,
  onContactSupport,
  onClose,
}: ManageSubscriptionDialogProps) {
  const { t } = useTranslation();
  const closeLabel = t('connectionModals.connect.close');
  const manageMode = resolveSubscriptionManageMode();

  if (import.meta.env.DEV) {
    console.log('[verifiedPaywall] manage dialog', {
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
      manageMode,
    });
  }

  const subtitle =
    manageMode === 'ios'
      ? t('verifiedPaywall.manage.iosSubtitle')
      : manageMode === 'android'
        ? t('verifiedPaywall.manage.genericSubtitle')
        : t('verifiedPaywall.manage.genericSubtitle');

  const storeCtaLabel =
    manageMode === 'ios'
      ? t('verifiedPaywall.manage.openAppleSettings')
      : t('settings.manageSubscription');

  return (
    <FixedSubscriptionModal
      header={
        <div className="relative px-4 pt-2 pb-1.5">
          <ModalCloseButton onClick={onClose} label={closeLabel} />
          <div className="flex justify-center mb-0.5">
            <CompactDialogIcon icon={<Shield className="text-white" />} />
          </div>
          <h2 className="text-sm font-bold text-center text-white leading-tight" dir="auto">
            {t('verifiedPaywall.manage.title')}
          </h2>
          <p className="text-center text-[11px] text-gray-400 mt-0.5" dir="auto">
            {subtitle}
          </p>
        </div>
      }
      body={
        <div className="px-4 pb-1.5">
          {manageMode === 'ios' ? (
            <p className="text-center text-[11px] text-gray-400 leading-snug" dir="auto">
              {t('verifiedPaywall.manage.appleCardBody')}
            </p>
          ) : manageMode === 'generic' ? (
            <p className="text-center text-[11px] text-gray-400 leading-snug" dir="auto">
              {t('verifiedPaywall.manage.genericBody')}
            </p>
          ) : null}
        </div>
      }
      footer={
        <div
          className="px-4 pt-2 space-y-1 border-t border-white/10 bg-slate-950/80"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
          {manageMode !== 'generic' && (
            <Button
              onClick={onOpenStoreSettings}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold h-9 text-sm rounded-xl"
            >
              <ExternalLink className="w-3.5 h-3.5 me-2" />
              {storeCtaLabel}
            </Button>
          )}
          <button
            type="button"
            onClick={onContactSupport}
            className="w-full text-center text-[11px] text-gray-400 hover:text-white py-0.5"
            dir="auto"
          >
            {t('verifiedPaywall.manage.supportLink')}
          </button>
        </div>
      }
    />
  );
}

export const VerifyPaywall: React.FC<VerifyPaywallProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  // V291.3.1: Dynamic price from Google Play
  const [productPrice, setProductPrice] = useState<string | null>(null);
  const [loadedProductId, setLoadedProductId] = useState<string | null>(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const [iosProductFetchStatus, setIosProductFetchStatus] = useState<IosProductFetchStatus>('loading');
  // CTO: Subscription details collapsible (collapsed by default = no-scroll paywall)
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  // V300: Identity-Aware Billing - Error dialogs
  const [showWrongUserDialog, setShowWrongUserDialog] = useState(false);
  const [showMissingIdDialog, setShowMissingIdDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const { user, userProfile, refreshProfile, signOut } = useAuth();
  // V210.2: Get entitlements refresh function for post-purchase refresh
  const { refresh: refreshEntitlements } = useEntitlements(user?.id || null);
  const { refreshNetwork } = useNetworkData();
  const navigate = useNavigate();

  // V210.2: Use centralized entitlements (replaces userProfile?.is_verified checks)
  const { entitlements } = useEntitlements(user?.id || null);
  // V291.2: Use unified verification helper (single source of truth)
  const isVerified = isVerifiedUI(entitlements, userProfile?.is_verified);

  // Sync effect: If DB says verified but entitlements lag, refresh once
  useEffect(() => {
    if (!user?.id) return;

    // If DB says verified but entitlements cache says not verified, refresh once.
    if (userProfile?.is_verified === true && entitlements?.isVerified !== true) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Entitlements stale vs users.is_verified=true — clearing cache + refreshing entitlements');
      }
      EntitlementService.clearCache(user.id);
      refreshEntitlements();
    }
  }, [user?.id, userProfile?.is_verified, entitlements?.isVerified, refreshEntitlements]);

  // V287: Runtime platform detection (takes precedence over build-time IS_WEB)
  // CRITICAL: Fixes issue where Android app shows web fallback due to server.url misconfiguration
  const isRuntimeWeb = useMemo(() => {
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    // If Capacitor says we're native, we're NOT web (regardless of build config)
    if (isNative) {
      if (IS_WEB) {
        console.warn('🚨 V287: Runtime platform (native) disagrees with build config (web)');
        console.warn('🚨 V287: Using runtime detection - showing native billing UI');
      }
      return false;
    }

    // Otherwise, trust build-time config
    return IS_WEB;
  }, []);

  // Runtime iOS detection (takes precedence over build-time IS_IOS)
  const isRuntimeIOS = useMemo(() => {
    if (Capacitor.isNativePlatform()) {
      return Capacitor.getPlatform() === 'ios';
    }
    return IS_IOS;
  }, []);

  // Derive the billing store label for use in UI copy
  const storeLabel = isRuntimeIOS ? 'App Store' : 'Google Play';
  const paywallBenefits = useMemo(() => getVerifiedPaywallBenefits(t), [t, i18n.language]);

  // V208.0: Analytics - paywall_viewed on mount
  useEffect(() => {
    trackEvent(AnalyticsEvents.PAYWALL_VIEWED).catch(() => {});
  }, []);

  // Prevent page scroll behind fixed subscription dialogs on iOS WebView
  useEffect(() => {
    if (!isVerified) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVerified]);

  if (import.meta.env.DEV) {
    console.log(`🎯 V287: Rendering VerifyPaywall`, {
      provider: PAYMENT_PROVIDER,
      isVerified,
      buildTimeWeb: IS_WEB,
      runtimeWeb: isRuntimeWeb,
      capacitorPlatform: Capacitor.getPlatform(),
      capacitorIsNative: Capacitor.isNativePlatform()
    });
  }

  // V189.3: Load subscription data if user is verified
  useEffect(() => {
    async function loadSubscription() {
      if (isVerified && user?.id) {
        try {
          if (import.meta.env.DEV) {
            console.log('🎯 V189.3: Loading subscription for verified user');
          }
          const sub = await UnifiedSubscriptionService.getCurrentSubscription(user.id);
          setSubscription(sub);
        } catch (error) {
          console.error('❌ V189.3: Failed to load subscription:', error);
        }
      }
    }
    loadSubscription();
  }, [isVerified, user?.id]);

  const fetchStoreProduct = useCallback(async () => {
    if (isRuntimeWeb) {
      setProductPrice('$10 USD');
      setPriceLoading(false);
      return;
    }

    const storeLog = isRuntimeIOS ? '[AppStore price]' : '[GooglePlay price]';
    const productId = isRuntimeIOS ? APPLE_VERIFIED_PRODUCT_ID : GOOGLE_PLAY_VERIFIED_PRODUCT_ID;

    if (isRuntimeIOS) {
      setIosProductFetchStatus('loading');
      setProductPrice(null);
      setLoadedProductId(null);
    } else {
      setPriceLoading(true);
    }

    try {
      console.log(`${storeLog} Fetching product price`);

      const billing = BillingManager.getInstance();
      await billing.initialize();

      const products = await billing.getProducts();
      const verifiedProduct = products.find((p) => p.id === productId);

      if (verifiedProduct?.localizedPrice) {
        console.log(`${storeLog} Got price:`, verifiedProduct.localizedPrice);
        setProductPrice(verifiedProduct.localizedPrice);
        setLoadedProductId(verifiedProduct.id);
        if (isRuntimeIOS) {
          setIosProductFetchStatus('ready');
        }
      } else if (verifiedProduct?.price) {
        setProductPrice(verifiedProduct.price);
        setLoadedProductId(verifiedProduct.id);
        if (isRuntimeIOS) {
          setIosProductFetchStatus('ready');
        }
      } else {
        console.warn(`${storeLog} No price returned`);
        setProductPrice(null);
        setLoadedProductId(null);
        if (isRuntimeIOS) {
          setIosProductFetchStatus('failed');
        }
      }
    } catch (error) {
      console.error(`${storeLog} Failed to fetch price:`, error);
      setProductPrice(null);
      setLoadedProductId(null);
      if (isRuntimeIOS) {
        setIosProductFetchStatus('failed');
      }
    } finally {
      if (!isRuntimeIOS) {
        setPriceLoading(false);
      }
    }
  }, [isRuntimeWeb, isRuntimeIOS]);

  useEffect(() => {
    fetchStoreProduct();
  }, [fetchStoreProduct]);

  const isIosNativePaywall = isRuntimeIOS && !isRuntimeWeb;
  const isIosProductReady =
    isIosNativePaywall &&
    iosProductFetchStatus === 'ready' &&
    loadedProductId === APPLE_VERIFIED_PRODUCT_ID &&
    productPrice !== null;
  const isIosProductFailed = isIosNativePaywall && iosProductFetchStatus === 'failed';

  const isSubscribeDisabled =
    loading ||
    (isIosNativePaywall
      ? iosProductFetchStatus === 'loading'
      : priceLoading);

  // V291.1: Removed legacy GooglePlayBillingService stub check
  // BillingManager handles all billing availability checks

  const handleUpgrade = async () => {
    console.log(`🎯 V199.0: Starting upgrade flow for ${PAYMENT_PROVIDER}`);

    // V210.2: Check if user is already verified (use entitlements, not userProfile.is_verified)
    if (isVerified) {
      console.log('🎯 V210.2: User already verified (entitlements.isVerified=true), blocking checkout');
      toast.info("You're already verified!", {
        description: 'Verified status and +100 trust score boost are active.',
        duration: 4000,
      });
      if (onClose) {
        onClose();
      }
      return;
    }

    setLoading(true);

    try {
      // V287: Use runtime platform detection instead of build-time IS_WEB
      // V209.6: Web billing disabled (Paddle removed); iOS stub not yet fully implemented
      if (isRuntimeWeb || IS_BILLING_DISABLED) {
        setLoading(false);
        toast.info('Get Verified on Mobile', {
          description: 'Verified status and +100 trust score boost are available on iPhone and Android.',
          duration: 6000,
        });
        if (onClose) {
          onClose();
        }
        return;
      }

      // ── iOS: App Store billing via StoreKit 2 ──────────────────────────────
      if (isRuntimeIOS) {
        if (iosProductFetchStatus === 'failed') {
          await fetchStoreProduct();
          setLoading(false);
          return;
        }

        if (
          iosProductFetchStatus !== 'ready' ||
          loadedProductId !== APPLE_VERIFIED_PRODUCT_ID ||
          !productPrice
        ) {
          setLoading(false);
          return;
        }

        console.log('🍎 Starting App Store billing flow');

        // Tracks whether StoreKit charged the user. When true, a later verification
        // failure must show a recoverable "pending verification" message rather than
        // the scary "subscription unavailable" copy (Apple Review safety).
        let storeKitPurchaseSucceeded = false;

        try {
          // Step 1: Initialize BillingManager (uses AppleBillingProvider)
          const billing = BillingManager.getInstance();
          await billing.initialize();

          // Step 2: Prepare post-verification success handler
          const handleAppleSubscriptionActivated = async (event: any) => {
            console.log('✅ Apple subscription_activated event received');
            if (onClose) onClose();

            toast.success('Processing your verification...', {
              description: 'This will take just a few seconds',
              duration: 3000,
            });

            // Poll for DB sync (same pattern as Android)
            const maxAttempts = 15;
            let verified = false;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              try {
                await refreshProfile();
                await refreshNetwork();
                await new Promise(r => setTimeout(r, 300));

                const { data: freshUser } = await supabase
                  .from('users')
                  .select('is_verified')
                  .eq('id', user!.id)
                  .single();

                if (freshUser?.is_verified) {
                  EntitlementService.clearCache(user!.id);
                  await refreshEntitlements();
                  verified = true;

                  toast.success(t('verifiedPaywall.toasts.purchaseSuccess'), {
                    duration: 5000,
                  });
                  trackEvent(AnalyticsEvents.PURCHASE_SUCCEEDED).catch(() => {});
                  break;
                }
              } catch { /* keep polling */ }

              const delay = Math.min(1500 * Math.pow(1.3, attempt - 1), 5000);
              await new Promise(r => setTimeout(r, delay));
            }

            if (!verified) {
              toast.info('Verification is taking a moment...', {
                description: 'Your subscription is processing. Reload the app if your badge doesn\'t appear.',
                duration: 8000,
              });
            }

            setLoading(false);
          };

          // Step 3: Integrity check (duplicate subscription guard)
          if (user?.id) {
            const existing = await UnifiedSubscriptionService.getCurrentSubscription(user.id);
            if (existing?.status === 'active') {
              toast.info('Subscription already active', {
                description: 'You already have an active Verified subscription.',
                duration: 5000,
              });
              setLoading(false);
              if (onClose) onClose();
              return;
            }
          }

          // Step 4: Launch StoreKit purchase sheet (product already loaded on paywall)
          console.log(`🍎 Launching App Store sheet for: ${loadedProductId}`);
          const result = await billing.launchPurchaseFlow(loadedProductId, {
            appAccountToken: user?.id, // Bind purchase to BlissNet user
          } as any);

          if (result.success && result.purchaseToken) {
            // StoreKit charged the user; any later failure is a verification issue.
            storeKitPurchaseSucceeded = true;
            // Step 6: Send JWS to server for verification
            console.log('🍎 Sending JWS to server for verification');
            const verifyResult = await invokeAuthedEdge<ValidatePurchaseEdgeResponse>('validate_purchase', {
              platform: 'ios',
              jwsRepresentation: result.purchaseToken,
              transactionId: result.transactionId,
              productId: APPLE_VERIFIED_PRODUCT_ID,
              isRestore: false,
            });

            if (verifyResult.error) {
              throw new Error(verifyResult.error.message ?? 'Server verification failed');
            }

            if (verifyResult.data?.verified && verifyResult.data.entitlementState === 'active') {
              // Finish the StoreKit transaction
              if (result.transactionId) {
                try {
                  const provider = (billing as any).provider;
                  if (provider?.finishTransaction) {
                    await provider.finishTransaction(result.transactionId);
                  }
                } catch { /* non-fatal */ }
              }

              // Verification already succeeded against server truth; run success UX flow.
              handleAppleSubscriptionActivated({});
            } else {
              throw new Error(verifyResult.data?.error ?? 'Server verification failed');
            }
          } else if (result.error?.code === 'E_USER_CANCELLED') {
            toast.info('Purchase cancelled', { duration: 3000 });
            setLoading(false);
          } else {
            throw result.error ?? new Error('Purchase failed');
          }

        } catch (error: any) {
          console.error('🍎 App Store billing error:', error);
          if (error?.code !== 'E_USER_CANCELLED') {
            if (storeKitPurchaseSucceeded) {
              // Purchase went through but verification failed/was delayed. Do not
              // grant access, but show recoverable copy instead of a scary error.
              toast.info(t('verified.actions.purchasePendingVerification'), { duration: 8000 });
            } else {
              toast.error(t('verifiedPaywall.toasts.purchaseFailed'), { duration: 5000 });
            }
          }
          setLoading(false);
        }
        return;
      }

      // V188.2: Extra safety - check for active subscription in database
      // This prevents the "Rani paid 4 times" scenario
      if (user?.id) {
        console.log('🎯 V188.2: Checking for existing active subscription...');
        const existingSubscription = await UnifiedSubscriptionService.getCurrentSubscription(user.id);

        if (existingSubscription && existingSubscription.status === 'active') {
          console.log('🎯 V188.2: Active subscription found, blocking duplicate purchase:', existingSubscription);
          toast.info('Subscription already active', {
            description: 'You already have an active Verified subscription.',
            duration: 5000,
          });
          setLoading(false);
          if (onClose) {
            onClose();
          }
          return;
        }

        console.log('🎯 V188.2: No active subscription found, proceeding with checkout');
      }

      if (IS_ANDROID && PAYMENT_PROVIDER === 'google_play') {
        // V287.3: Google Play Billing via BillingManager with Paddle-style verification UX
        console.log('🎯 V287.3: Starting Google Play billing flow');

        // V287.3: Event cleanup function (scoped outside try-catch for cleanup in finally)
        let eventCleanup: (() => void) | null = null;

        try {
          // --------------------------------------------------------------------
          // Step 1: Initialize BillingManager
          // --------------------------------------------------------------------
          console.log('📱 V287.3: Initializing BillingManager...');
          const billing = BillingManager.getInstance();
          await billing.initialize();
          console.log('✅ V287.3: BillingManager initialized');

          // --------------------------------------------------------------------
          // Step 2: Set up event listener for subscription_activated
          // --------------------------------------------------------------------

          const handleSubscriptionActivated = async () => {
            console.log('✅ V287.3: Subscription activated event received');

            // V210.9: Check if PurchaseSuccessHandler already handled this
            if ((window as any).__v210_9_purchase_handled) {
              console.log('✅ V210.9: Purchase already handled by PurchaseSuccessHandler, skipping polling');
              if (onClose) {
                onClose();
              }
              return;
            }

            // Close paywall dialog immediately
            if (onClose) {
              onClose();
            }

            // Show processing toast (same as Paddle)
            toast.success('Processing your verification...', {
              description: 'This will take just a few seconds',
              duration: 3000,
            });

            // V287.3: Poll for verification (same logic as Paddle lines 247-294)
            const maxAttempts = 15;
            const baseDelay = 1500;
            const maxDelay = 5000;
            let verified = false;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              console.log(`🔄 V287.3: Poll attempt ${attempt}/${maxAttempts}`);

              try {
                // Force fresh data from database
                await refreshProfile();
                await refreshNetwork();

                // Wait for state propagation
                await new Promise(resolve => setTimeout(resolve, 300));

                // Check database directly
                const { data: freshProfile } = await supabase
                  .from('users')
                  .select('is_verified, trust_score, verified_at')
                  .eq('id', user?.id)
                  .single();

                console.log('🔍 V287.3: Fresh profile data:', {
                  is_verified: freshProfile?.is_verified,
                  trust_score: freshProfile?.trust_score,
                  verified_at: freshProfile?.verified_at
                });

                if (freshProfile?.is_verified === true) {
                  console.log('✅ V287.3: Verification detected!');
                  verified = true;
                  break;
                }

                // Exponential backoff
                const delay = Math.min(baseDelay * attempt, maxDelay);
                console.log(`⏳ V287.3: Waiting ${delay}ms before next attempt...`);
                await new Promise(resolve => setTimeout(resolve, delay));

              } catch (error) {
                console.error(`❌ V287.3: Poll attempt ${attempt} failed:`, error);
              }
            }

            if (verified) {
              // Success! (same as Paddle lines 296-326)
              console.log('✅ V287.3: Verification complete, showing success');

              // V210.2: Clear entitlement cache and refresh
              if (user?.id) {
                EntitlementService.clearCache(user.id);
                await refreshEntitlements();
                console.log('✅ V210.2: Entitlements refreshed after purchase');
              }

              // Force final refresh
              await refreshProfile();
              await refreshNetwork();

              // Show success toast
              toast.success('Verification Complete!', {
                description: '🎉 You\'re now verified!',
                duration: 4000,
              });

              // Navigate to profile with verified flag
              navigate('/profile?verified=1', { replace: true });

              // V210.9: Removed automatic reload - PurchaseSuccessHandler controls reload timing
              // This prevents race condition where reload happens before DB is updated
              console.log('✅ V210.9: Verification complete - PurchaseSuccessHandler will handle reload');

            } else {
              // Timeout (same as Paddle lines 327-338)
              console.warn('⚠️ V287.3: Verification polling timed out');

              toast.info('Verification processing...', {
                description: 'Taking longer than expected. Please refresh the page in a moment.',
                duration: 6000,
              });

              navigate('/profile?verified=1', { replace: true });
            }

            // Cleanup event listener
            if (eventCleanup) {
              eventCleanup();
            }
          };

          // Register event listener
          eventCleanup = billing.addEventListener((event) => {
            if (event.type === 'subscription_activated') {
              handleSubscriptionActivated();
            }
          });

          // --------------------------------------------------------------------
          // Step 3: Verify product availability (optional but recommended)
          // --------------------------------------------------------------------
          try {
            const products = await billing.getProducts();
            const verifiedProduct = products.find(p => p.id === GOOGLE_PLAY_VERIFIED_PRODUCT_ID);

            if (verifiedProduct) {
              console.log('✅ V287.3: Product found:', {
                id: verifiedProduct.id,
                title: verifiedProduct.title,
                price: verifiedProduct.localizedPrice,
              });
            } else {
              console.warn(`⚠️ V287.3: ${GOOGLE_PLAY_VERIFIED_PRODUCT_ID} product not found in available products`);
              // Continue anyway - purchaseVerified() will handle the error
            }
          } catch (productError) {
            console.warn('⚠️ V287.3: Could not fetch products (non-fatal):', productError);
            // Continue anyway - purchaseVerified() will handle product lookup
          }

          // --------------------------------------------------------------------
          // V317.2: Step 4: Preflight Integrity Check (AUDIT ONLY - NON-BLOCKING)
          // CRITICAL: Follows V314 "graceful degradation" principle
          // Purpose: Log purchase integrity for audit, but NEVER block legitimate users
          // --------------------------------------------------------------------
          console.log('[Purchase] V317.2: Tapped plan - Triggering Integrity (Purchase - Audit Only)');

          let integrityCheckId: string | null = null;

          try {
            // V317.2: Import PlayIntegrityService dynamically to avoid circular deps
            const { PlayIntegrityService } = await import('@/services/PlayIntegrityService');

            // V317.2: Explicit Purchase Check - send 'purchase' verbatim
            console.log('[Purchase] V317.2: Calling PlayIntegrityService.run("purchase", "plan_tap_v317_2")');
            const integrityResult = await PlayIntegrityService.run('purchase', 'plan_tap_v317_2');

            // V317.2: Log result (production-safe: no tokens, no sensitive data)
            const checkId = integrityResult?.check_id ?? 'null';
            const checkIdLast6 = checkId !== 'null' && checkId.length >= 6 ? checkId.slice(-6) : checkId;
            const resultOk = integrityResult?.ok ?? false;
            const resultReason = integrityResult?.reason ?? 'unknown';

            console.log(`[Purchase] V317.2 Preflight result: ok=${resultOk} reason=${resultReason} check_id=${checkIdLast6}`);

            // V317.2 GRACEFUL DEGRADATION: Log but NEVER block
            // This matches bootstrap/login behavior - audit only
            if (Capacitor.getPlatform() === 'android') {
              if (!integrityResult) {
                console.warn('[Purchase] V317.2: Integrity check returned null (graceful pass - audit logged)');
              } else if (!resultOk) {
                console.warn('[Purchase] V317.2: Integrity check failed (graceful pass - audit logged)', {
                  reason: resultReason,
                  check_id: checkIdLast6
                });
              } else {
                console.log(`[Purchase] V317.2: Integrity check passed, proceeding to billing. check_id=${checkIdLast6}`);
              }

              // V317.2: Store check_id for audit trail (non-blocking)
              integrityCheckId = checkId !== 'null' ? checkId : null;
            } else {
              console.log('[Purchase] V317.2: Preflight check completed (non-Android platform)');
            }

          } catch (integrityError) {
            // V317.2: Catch errors but NEVER block (graceful degradation)
            console.error('[Purchase] V317.2: Preflight integrity check threw error (graceful pass):', integrityError);
            // Continue to purchase - error is logged for audit but doesn't block user
          }

          // V317.2: ALWAYS proceed to billing (integrity check is audit-only)
          console.log('[Purchase] V317.2: Proceeding to Google Play billing (integrity check complete, non-blocking)');

          // --------------------------------------------------------------------
          // Step 5: Launch Google Play billing sheet
          // --------------------------------------------------------------------
          console.log(`📱 V287.3: Launching Google Play billing sheet for: ${GOOGLE_PLAY_VERIFIED_PRODUCT_ID}`);

          const result = await billing.purchaseVerified();

          // --------------------------------------------------------------------
          // Step 6: Handle purchase result
          // --------------------------------------------------------------------
          // V287.3: Purchase flow is now event-driven via subscription_activated listener
          // The handleSubscriptionActivated callback above handles all verification UX

          if (result.success) {
            console.log('✅ V287.3: Purchase flow initiated successfully');
            console.log('⏳ V287.3: Waiting for subscription_activated event...');
            // Event listener will handle the rest

          } else if (result.error) {
            // Purchase failed with error
            console.error('❌ V287.3: Purchase failed:', result.error);

            // Cleanup event listener
            if (eventCleanup) {
              eventCleanup();
            }

            throw result.error;
          } else {
            // Purchase returned neither success nor error (shouldn't happen)
            console.warn('⚠️ V287.3: Unexpected purchase result:', result);

            // Cleanup event listener
            if (eventCleanup) {
              eventCleanup();
            }

            throw new Error('Unexpected purchase result. Please try again.');
          }

        } catch (error: any) {
          console.error('❌ V287.3: Google Play Billing error:', error);

          // --------------------------------------------------------------------
          // Step 6: User-friendly error handling
          // --------------------------------------------------------------------
          let errorMessage: string;
          let errorTitle = 'Purchase Failed';

          let reasonCode: 'cancelled' | 'payment_declined' | 'network_error' | 'unknown' = 'unknown';
          switch (error?.code) {
            case 'E_USER_CANCELLED':
              reasonCode = 'cancelled';
              errorTitle = 'Purchase Cancelled';
              errorMessage = 'You cancelled the purchase.';
              trackEvent(AnalyticsEvents.PURCHASE_FAILED, { reason_code: reasonCode }).catch(() => {});
              // Don't show alert for cancellation - just toast
              toast.info(errorTitle, {
                description: errorMessage,
                duration: 3000,
              });
              setLoading(false);
              return; // Exit early for cancellation

            case 'E_NETWORK_ERROR':
              reasonCode = 'network_error';
              errorMessage = 'Network error. Please check your connection and try again.';
              break;

            case 'E_ITEM_ALREADY_OWNED':
            case 'ITEM_ALREADY_OWNED':
              // V301: Trigger restore flow instead of just refreshing
              errorTitle = 'Already Subscribed';
              errorMessage = 'You already have an active subscription. Restoring your purchase...';
              toast.info(errorTitle, {
                description: errorMessage,
                duration: 4000,
              });

              // V301: Trigger restore flow to verify and acknowledge
              console.log('🔄 V301: ITEM_ALREADY_OWNED - triggering restore flow');
              await handleRestorePurchases();
              return; // Exit early - restore flow handles everything

            case 'E_DEVELOPER_ERROR':
              reasonCode = 'payment_declined';
              errorMessage = 'Configuration error. Please contact support.';
              console.error('V287.3: Developer error - check Play Console setup:', error);
              break;

            case 'E_SERVICE_DISCONNECTED':
              reasonCode = 'network_error';
              errorMessage = 'Google Play services unavailable. Please try again later.';
              break;

            default:
              reasonCode = 'payment_declined';
              errorMessage = error?.message || 'Unable to process payment. Please try again later.';
          }

          trackEvent(AnalyticsEvents.PURCHASE_FAILED, { reason_code: reasonCode }).catch(() => {});

          // Show error toast
          toast.error(errorTitle, {
            description: errorMessage,
            duration: 5000,
          });

          // For non-cancellation errors, also show native alert as fallback
          if (error?.code !== 'E_USER_CANCELLED' && error?.code !== 'E_ITEM_ALREADY_OWNED') {
            alert(`⚠️ ${errorTitle}\n\n${errorMessage}\n\nPlease try again or contact support at blissnetapp@gmail.com`);
          }
        } finally {
          // V287.3: Cleanup event listener
          if (eventCleanup) {
            console.log('🧹 V287.3: Cleaning up event listener');
            eventCleanup();
          }
          setLoading(false);
        }
      }
    } catch (error: any) {
      console.error('🎯 V185.6: Error starting upgrade flow:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`⚠️ Something went wrong: ${errorMessage}\n\nPlease try again or contact support at blissnetapp@gmail.com`);
      setLoading(false);
    }
  };

  const handleManageBilling = () => {
    if (import.meta.env.DEV) console.log('🎯 V196.9: Opening subscription management');
    setShowManageDialog(true);
  };

  const handleOpenStoreSettings = () =>
    openSubscriptionStoreSettings(() => navigate('/profile?manage=subscription'));

  const handleContactSupport = () => contactSubscriptionSupport();

  // V210.2: Show verified status or manage subscription dialog (fixed footer CTAs)
  if (isVerified) {
    return (
      <>
        {showManageDialog ? (
          <ManageSubscriptionDialog
            onOpenStoreSettings={() => { void handleOpenStoreSettings(); }}
            onContactSupport={() => { void handleContactSupport(); }}
            onClose={() => setShowManageDialog(false)}
          />
        ) : (
          <VerifiedStatusDialog
            showRenewalNote={Boolean(subscription?.current_period_end)}
            onManage={handleManageBilling}
            onDone={() => {
              if (import.meta.env.DEV) console.log('🎯 V196.8: Got it button clicked');
              if (onClose) onClose();
            }}
            onClose={onClose}
          />
        )}
      </>
    );
  }

  // V300: Identity-Aware Billing - Switch User handler
  const handleSwitchUser = async () => {
    console.log('[V300] Switch user initiated - signing out');
    try {
      await signOut();
      toast.info('Signed out successfully', {
        description: 'Please sign in with the correct account.',
        duration: 3000,
      });
      if (onClose) onClose();
    } catch (error) {
      console.error('[V300] Sign out failed:', error);
      toast.error('Could not sign out', {
        description: 'Please try again.',
        duration: 3000,
      });
    }
  };

  // V300: Enhanced Restore Purchases handler with Identity-Aware error handling
  const handleRestorePurchases = async () => {
    console.log('[V300] Restore purchases initiated (Identity-Aware)');

    if (!user?.id) {
      toast.error('Not signed in', {
        description: 'Please sign in to restore purchases.',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);

      // V300: Query Google Play for existing purchases
      const billing = BillingManager.getInstance();
      await billing.initialize();

      const restoredPurchases = await billing.restorePurchases();
      console.log('[V300] Restored purchases:', restoredPurchases.length);

      if (restoredPurchases.length === 0) {
        toast.info('No active subscription found', {
          description: 'If you recently subscribed, please wait a moment and try again.',
          duration: 5000,
        });
        setLoading(false);
        return;
      }

      // Verify each purchase with the appropriate backend function
      for (const purchase of restoredPurchases) {
        try {
          if (isRuntimeIOS) {
            // ── Apple: send JWS to apple-verify-purchase ────────────────────
            if (!purchase.transactionId) {
              throw new Error('missing_restore_transaction_id');
            }

            const verifyResult = await invokeAuthedEdge<ValidatePurchaseEdgeResponse>('validate_purchase', {
              platform: 'ios',
              jwsRepresentation: purchase.purchaseToken, // JWS stored as purchaseToken
              transactionId: purchase.transactionId,
              productId: APPLE_VERIFIED_PRODUCT_ID,
              isRestore: true,
            });

            if (verifyResult.error) {
              throw new Error(verifyResult.error.message ?? 'Apple verification failed');
            }

            if (!verifyResult.data?.verified || verifyResult.data.entitlementState !== 'active') {
              throw new Error(verifyResult.data?.error ?? 'Apple verification failed');
            }
            console.log('[Restore] ✅ Apple purchase verified');

          } else {
            // ── Android: send to google-verify-purchase ──────────────────────
            let response: { data: unknown; error: { code?: string; message?: string; requestId?: string } | null; requestId?: string };
            try {
              response = await invokeAuthedEdge(
                'google-verify-purchase',
                {
                  purchaseToken: purchase.purchaseToken,
                  productId: 'verified_monthly',
                  packageName: 'com.blissnet.app',
                }
              );
            } catch (invokeErr) {
              if (invokeErr instanceof InvokeNotAuthenticatedError) {
                toast.error('Session expired. Please sign in again and tap Restore Purchases.', {
                  description: (invokeErr as any).requestId ? `Request ID: ${(invokeErr as any).requestId}` : undefined,
                  duration: 4000,
                });
                setLoading(false);
                return;
              }
              throw invokeErr;
            }

            if (response.error?.code === 'NOT_AUTHENTICATED' || response.error?.code === 'UNAUTHORIZED') {
              toast.error('Session expired. Please sign in again and tap Restore Purchases.', { duration: 4000 });
              setLoading(false);
              return;
            }

            if (response.error) {
              const errorCode = response.error.code || response.error.message;
              if (errorCode === 'PURCHASE_BELONGS_TO_DIFFERENT_USER') {
                setShowWrongUserDialog(true);
                setLoading(false);
                return;
              }
              if (errorCode === 'MISSING_OBFUSCATED_ID') {
                setShowMissingIdDialog(true);
                setLoading(false);
                return;
              }
              throw new Error(response.error.message || 'Verification failed');
            }

            console.log('[Restore] ✅ Google purchase verified');

            // Acknowledge the Android purchase
            try {
              const GooglePlayBilling = (await import('@/plugins/GooglePlayBilling')).default;
              await GooglePlayBilling.acknowledgePurchase({ purchaseToken: purchase.purchaseToken });
            } catch (ackError) {
              console.warn('[Restore] ⚠️ Acknowledgment failed (may already be acknowledged):', ackError);
            }
          }

        } catch (verifyError) {
          console.error('[Restore] Purchase verification failed:', verifyError);
          // Continue to next purchase
        }
      }

      // Clear cache and refresh entitlements
      if (user?.id) {
        EntitlementService.clearCache(user.id);
      }
      await refreshEntitlements();
      await refreshProfile();
      await refreshNetwork();

      // Check if now verified
      const { data: freshProfile } = await supabase
        .from('users')
        .select('is_verified')
        .eq('id', user?.id)
        .single();

      if (freshProfile?.is_verified) {
        toast.success(t('verifiedPaywall.toasts.restoreSuccess'), {
          duration: 4000,
        });
        if (onClose) onClose();
      } else {
        toast.info('No active subscription found', {
          description: 'If you recently subscribed, please wait a moment and try again.',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('[V300] Restore failed:', error);
      toast.error(t('verifiedPaywall.toasts.restoreFailed'), {
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // V300: Identity-Aware Billing - Render error dialogs
  const renderErrorDialogs = () => (
    <>
      {/* V300: Wrong User Dialog */}
      <Dialog open={showWrongUserDialog} onOpenChange={setShowWrongUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              Wrong Account
            </DialogTitle>
            <DialogDescription className="text-gray-300 pt-2">
              This subscription belongs to a different BlissNet account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-400">
              The {storeLabel} purchase you're trying to restore was made by a different BlissNet user.
              Please sign in to the account that originally started the subscription.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleSwitchUser}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Switch Account
              </Button>
              <Button
                onClick={() => setShowWrongUserDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* V300: Missing Obfuscated ID Dialog */}
      <Dialog open={showMissingIdDialog} onOpenChange={setShowMissingIdDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertCircle className="w-5 h-5" />
              Purchase Link Missing
            </DialogTitle>
            <DialogDescription className="text-gray-300 pt-2">
              We couldn't link this purchase to your BlissNet account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-400">
              This purchase is missing the required account identifier. This may happen with very old purchases
              or if the purchase was made before our identity verification system was implemented.
            </p>
            <p className="text-sm text-gray-400">
              Please contact support at <a href="mailto:support@blissnet.app" className="text-purple-400 underline">support@blissnet.app</a> or
              make a new purchase to continue.
            </p>
            <Button
              onClick={() => setShowMissingIdDialog(false)}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {t('verifiedPaywall.verified.doneButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  // V291.3: Show purchase flow if not verified - using enhanced VerifiedDialogLayout
  return (
    <>
      {renderErrorDialogs()}
      <PaywallDialogShell>
      <VerifiedDialogLayout
        variant="paywall"
        accentColor="purple"
        title={t('verified.title')}
        subtitle={t('verified.subtitle')}
        icon={<Shield className="w-12 h-12" />}
        bullets={paywallBenefits}
        primaryCtaLabel={
          loading
            ? t('verified.actions.processing')
            : isRuntimeWeb
              ? t('verified.platform.availableOnIphoneAndroid')
              : isIosNativePaywall
                ? iosProductFetchStatus === 'loading'
                  ? t('verified.actions.loadingSubscription')
                  : iosProductFetchStatus === 'failed'
                    ? t('verified.actions.tryAgain')
                    : isIosProductReady && productPrice
                      ? t('verified.actions.subscribeMonthly', { price: productPrice })
                      : t('verified.actions.loadingSubscription')
                : priceLoading
                  ? t('verified.actions.loading')
                  : !productPrice
                    ? t('verified.actions.loading')
                    : t('verified.actions.subscribeMonthly', { price: productPrice })
        }
        onPrimaryCta={handleUpgrade}
        primaryCtaLoading={loading}
        primaryCtaDisabled={isSubscribeDisabled}
        secondaryCtaLabel={t('verified.actions.maybeLater')}
        onSecondaryCta={onClose}
        showCloseButton={false}
        footerNote={
          <div className="space-y-3 sm:space-y-4">
            {isIosProductFailed && (
              <p className="text-center text-xs text-amber-300/90 leading-relaxed">
                {t('verified.actions.subscriptionCouldNotLoad')}
              </p>
            )}

            {/* V300: Identity Banner - Shows who is being verified vs who pays */}
            {!isRuntimeWeb && user && (
              <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-xl p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-purple-300 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2 text-start">
                    <div>
                      <div className="text-xs text-purple-200 font-medium mb-1">{t('verified.account.verifyingAccount')}</div>
                      <div className="text-sm font-semibold text-white">
                        {userProfile?.name || user.email}
                      </div>
                      {userProfile?.name && user.email && (
                        <div className="text-xs text-gray-400">{user.email}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-300 leading-relaxed">
                      {t('verified.account.billedVia', { store: storeLabel })}
                      <br />
                      <span className="text-gray-400">{t('verified.account.checkoutAccountNote')}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleSwitchUser}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-3
                             bg-slate-800/60 hover:bg-slate-700/80 active:bg-slate-600/80
                             border border-white/10 rounded-lg text-gray-300 hover:text-white
                             text-xs font-medium transition-all duration-200"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>{t('verified.account.notYouSwitchUser')}</span>
                </button>
              </div>
            )}

            {/* CTO: Restore Purchases Button - required + compact */}
            {!isRuntimeWeb && (
              <button
                onClick={handleRestorePurchases}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4
                         bg-slate-800/60 hover:bg-slate-700/80 active:bg-slate-600/80
                         border border-white/10 rounded-xl text-gray-300 hover:text-white
                         text-sm font-medium transition-all duration-200"
              >
                <RotateCcw className="w-4 h-4" />
                <span>{t('settings.restorePurchases')}</span>
              </button>
            )}

            {/* CTO: Subscription Details — collapsible (collapsed by default = no-scroll paywall) */}
            {!isRuntimeWeb && (
              <div>
                <button
                  onClick={() => setShowSubscriptionDetails(prev => !prev)}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-amber-400/80 hover:text-amber-300 transition-colors duration-200 py-1"
                >
                  <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>
                    {showSubscriptionDetails
                      ? t('verified.subscriptionDetails.hide')
                      : t('verified.subscriptionDetails.view')}{' '}
                    {t('verified.subscriptionDetails.title')}
                  </span>
                  <span className="text-[10px]">{showSubscriptionDetails ? '▲' : '▼'}</span>
                </button>

                {showSubscriptionDetails && (
                  <div className="mt-2 bg-slate-800/80 border border-amber-500/30 rounded-xl p-3">
                    <ul className="space-y-1.5 text-xs text-gray-200 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 mt-0.5">•</span>
                        <span>{t('verified.subscriptionDetails.autoRenewal')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 mt-0.5">•</span>
                        <span>
                          {t('verified.subscriptionDetails.billing', {
                            price: isIosNativePaywall
                              ? iosProductFetchStatus === 'loading'
                                ? '...'
                                : iosProductFetchStatus === 'failed'
                                  ? t('verified.actions.subscriptionCouldNotLoad')
                                  : (productPrice ?? '...')
                              : priceLoading
                                ? '...'
                                : (productPrice ?? t('verified.actions.loading')),
                            store: storeLabel,
                          })}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 mt-0.5">•</span>
                        <span>{t('verified.subscriptionDetails.cancel', { store: storeLabel })}</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Privacy/Terms + security — compact single row */}
            <div className="text-center text-xs text-gray-400 leading-relaxed space-y-1">
              {isRuntimeWeb ? (
                <div className="flex items-center justify-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5" />
                  <span>{t('verified.platform.availableOnApps')}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  <span>{t('verified.legal.billedSecurely', { store: storeLabel })}</span>
                </div>
              )}
              <div dir="auto">
                {t('verified.legal.bySubscribing')}{' '}
                <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">{t('verified.legal.terms')}</a>
                {' '}{t('verified.legal.and')}{' '}
                <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline underline-offset-2">{t('verified.legal.privacyPolicy')}</a>.
              </div>
            </div>
          </div>
        }
      />
      </PaywallDialogShell>
    </>
  );
};

export default VerifyPaywall;

